/**
 * useUserView — shared context for the "viewing as" user toggle.
 *
 * Views:
 *   '<userName>' — Show that user's metadata, all items
 *   'pod'        — Only items where ALL users have engaged, combined display
 *   'all'        — Everything in DB, combined display with per-user emoji indicators
 *
 * Data model:
 *   Items carry a `_userData` map populated by the collection adapter's fetch:
 *     item._userData = { benny: { status, rating, ... }, gareth: { ... } }
 *   projectUserFields() maps the active user's data onto generic field names.
 *
 * Users are loaded dynamically from PB via the UsersProvider (see useUsers.jsx).
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUsers, deriveViewOptions } from './useUsers.jsx';

/**
 * Returns true if the view uses combined display (both users' data with emoji indicators).
 */
export function isCombinedView(activeView) {
    return activeView === 'all' || activeView === 'pod';
}

// ── Context ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'tracker:userView';

const UserViewContext = createContext({
    activeView: null,
    setActiveView: () => {},
});

export function UserViewProvider({ children, currentUserName, podMemberNames }) {
    const { users } = useUsers();

    const viewOptions = deriveViewOptions(users, {
        currentUserName,
        podMemberNames,
    });

    const [activeView, setActiveViewState] = useState(() => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const urlUser = urlParams.get('user');
            if (urlUser && viewOptions.includes(urlUser)) return urlUser;
            const stored = localStorage.getItem(STORAGE_KEY);
            if (viewOptions.includes(stored)) return stored;
            return currentUserName || (users.length > 0 ? users[0].name : 'all');
        } catch {
            return 'all';
        }
    });

    // When pod context loads after mount, validate the active view
    useEffect(() => {
        if (viewOptions.length > 0 && !viewOptions.includes(activeView)) {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored && viewOptions.includes(stored)) {
                setActiveViewState(stored);
            } else {
                setActiveViewState(currentUserName || viewOptions[0]);
            }
        }
    }, [viewOptions.join(','), currentUserName]);

    const setActiveView = useCallback((view) => {
        setActiveViewState(view);
        try { localStorage.setItem(STORAGE_KEY, view); } catch {}
    }, []);

    return (
        <UserViewContext.Provider value={{ activeView, setActiveView, viewOptions }}>
            {children}
        </UserViewContext.Provider>
    );
}

export function useUserView() {
    return useContext(UserViewContext);
}

// ── Field projection helpers ─────────────────────────────────────────────────

/**
 * Resolves which user to read fields for.
 * A user name → that user
 * 'all' | 'pod' → null (combined view)
 */
function resolveUser(activeView) {
    if (activeView === 'pod' || activeView === 'all') return null;
    return activeView; // user name
}

/**
 * Projects user data onto generic field names for a single item.
 *
 * Items should have a `_userData` map:
 *   item._userData = { benny: { status: 'Playing', ... }, gareth: { ... } }
 *
 * For single-user views: maps that user's data to generic fields.
 * For combined views: uses first user with data as primary, keeps _userData for UI.
 *
 * @param {Object} rawItem - Item with _userData map
 * @param {string} collectionType - 'videogames' | 'movies' | 'seasons'
 * @param {string} activeView - User name or 'pod' | 'all'
 * @param {Array} [users] - Users array (for combined view ordering)
 * @returns {Object} Item with generic field names projected
 */
export function projectUserFields(rawItem, collectionType, activeView, users) {
    const item = { ...rawItem };
    const userData = item._userData;
    const user = resolveUser(activeView);

    // If _userData is present and has entries, use the new system
    if (userData && Object.keys(userData).length > 0) {
        const fieldNames = Object.keys(userData[Object.keys(userData)[0]] || {});

        if (user && userData[user]) {
            // Single-user view
            for (const field of fieldNames) {
                item[field] = userData[user][field] ?? null;
            }
        } else {
            // Combined view: use first user with data as primary
            const userNames = users ? users.map((u) => u.name) : Object.keys(userData);
            for (const field of fieldNames) {
                if (field.toLowerCase().includes('date') || field === 'watchedDate' || field === 'lastPlayed') {
                    // Use earliest date
                    let earliest = null;
                    for (const u of userNames) {
                        const val = userData[u]?.[field];
                        if (val) {
                            if (!earliest || new Date(val) < new Date(earliest)) {
                                earliest = val;
                            }
                        }
                    }
                    item[field] = earliest;
                } else {
                    // Use first non-null value
                    item[field] = null;
                    for (const u of userNames) {
                        const val = userData[u]?.[field];
                        if (val != null && val !== '') {
                            item[field] = val;
                            break;
                        }
                    }
                }
            }
        }

        return item;
    }

    return item;
}

// ── Engagement helpers ──────────────────────────────────────────────────────

/**
 * Checks if an item has engagement from a specific user.
 *
 * @param {Object} item - Item with _userData
 * @param {string} userName - User name to check
 * @param {string} collectionType - 'videogames' | 'movies' | 'seasons'
 * @param {string[]} [notEngagedStatuses] - Statuses that don't count as engagement
 * @returns {boolean}
 */
export function userHasEngaged(item, userName, collectionType, notEngagedStatuses = []) {
    // New path: check _userData
    if (item._userData) {
        const ud = item._userData[userName];
        if (!ud) return false;
        const engagementField = collectionType === 'videogames' ? 'status'
            : collectionType === 'boardgames' ? 'firstPlayed'
            : collectionType === 'books' ? 'status'
            : 'watchedDate';
        const val = ud[engagementField];
        if (val == null || val === '') return false;
        if (notEngagedStatuses.includes(val)) return false;
        return true;
    }

    return false;
}

/**
 * Filters items for pod view — items where enough users have engaged.
 *
 * @param {Array} items - Items with _userData
 * @param {string} collectionType
 * @param {string[]} [notEngagedStatuses]
 * @param {Array} [users] - Users array
 * @param {number|null} [minParticipants] - Minimum engaged users required.
 *   null/0 = require ALL users (default, backward compat for couples).
 *   2+ = require at least N participants (useful for larger groups).
 */
export function filterPodItems(items, collectionType, notEngagedStatuses = [], users, minParticipants) {
    const userNames = users?.length > 0 ? users.map((u) => u.name) : [];
    const threshold = minParticipants && minParticipants > 0
        ? minParticipants
        : userNames.length; // default: require all

    return items.filter((item) => {
        const engagedCount = userNames.filter(
            (u) => userHasEngaged(item, u, collectionType, notEngagedStatuses)
        ).length;
        return engagedCount >= threshold;
    });
}

/**
 * Filters items to only those where the active user has _userData.
 * In single-user view, removes items the user hasn't interacted with.
 * In combined views ('all'/'pod'), returns items unchanged.
 *
 * @param {Array} items - Items with _userData attached
 * @param {string} activeView - User name or 'all'/'pod'
 * @returns {Array} Filtered items
 */
export function filterToUserItems(items, activeView) {
    if (isCombinedView(activeView)) return items;
    return items.filter((item) => {
        const ud = item._userData?.[activeView];
        return ud && Object.values(ud).some((v) => v != null && v !== '');
    });
}

/**
 * Merges user data records from join tables onto item records.
 * Creates the _userData map that projectUserFields reads.
 *
 * @param {Array} items - Raw item records from PB (videogames, movies, etc.)
 * @param {Array} userDataRecords - Records from user_games/user_movies/user_seasons (with expand: 'user')
 * @param {string} itemIdField - Field name on the user data record that references the item ('game', 'movie', 'season')
 * @param {Array} userFields - Field names to extract (e.g. ['status', 'rating', 'lastPlayed', ...])
 * @param {Array} users - Users array from useUsers()
 * @param {Array} [allowedUserIds] - If provided, only include data from these user IDs
 * @returns {Array} Items with _userData attached
 */
export function mergeUserData(items, userDataRecords, itemIdField, userFields, users, allowedUserIds) {
    // Build a map: itemId → { userName: { field: value, ... } }
    const userDataMap = {};

    // Pre-build allowed set for O(1) lookup
    const allowedSet = allowedUserIds ? new Set(allowedUserIds) : null;

    for (const udr of userDataRecords) {
        const itemId = udr[itemIdField];
        const userId = udr.user;

        // Skip users not in the allowed set
        if (allowedSet && !allowedSet.has(userId)) continue;

        // Skip Hidden watch records — they've been deleted from the user's perspective
        if (udr.status === 'Hidden') continue;

        // Find user name from userId
        const user = users.find((u) => u.id === userId);
        if (!user) continue;

        if (!userDataMap[itemId]) userDataMap[itemId] = {};
        const fields = {};
        for (const f of userFields) {
            fields[f] = udr[f] ?? null;
        }

        // Extract year from the watch date for year-tab filtering
        const watchYear = fields.watchedDate
            ? new Date(fields.watchedDate).getFullYear()
            : null;

        if (!userDataMap[itemId][user.name]) {
            // First record for this (item, user) — store it with watchCount: 1
            userDataMap[itemId][user.name] = {
                ...fields,
                watchCount: 1,
                watchRecordId: udr.id,
                allWatchYears: watchYear ? [watchYear] : [],
                watchDatesByYear: watchYear ? { [watchYear]: fields.watchedDate } : {},
            };
        } else {
            // Multiple records (rewatches): keep the most recent by watchedDate,
            // but always increment the count and accumulate all years + per-year dates
            const existing = userDataMap[itemId][user.name];
            const newCount = (existing.watchCount || 1) + 1;
            if (watchYear) {
                if (!existing.allWatchYears?.includes(watchYear)) {
                    existing.allWatchYears = [...(existing.allWatchYears || []), watchYear];
                }
                if (!existing.watchDatesByYear) existing.watchDatesByYear = {};
                // Keep the most recent date within each year
                const prev = existing.watchDatesByYear[watchYear];
                if (!prev || fields.watchedDate > prev) {
                    existing.watchDatesByYear[watchYear] = fields.watchedDate;
                }
            }
            const newDate = fields.watchedDate;
            const existingDate = existing.watchedDate;
            if (!existingDate || (newDate && newDate > existingDate)) {
                // New record is more recent — use its fields but keep the running count + years + dates
                const years = existing.allWatchYears;
                const datesByYear = existing.watchDatesByYear;
                userDataMap[itemId][user.name] = { ...fields, watchCount: newCount, watchRecordId: udr.id, allWatchYears: years, watchDatesByYear: datesByYear };
            } else {
                // Existing record is more recent — just bump the count
                existing.watchCount = newCount;
            }
        }
    }

    // Attach _userData to each item
    return items.map((item) => ({
        ...item,
        _userData: userDataMap[item.id] || {},
    }));
}

export default useUserView;
